// Recent Routing Decisions widget — member dashboard read-only panel.
// Proof of Smart Router activity. Read-only, no writes.

import { useEffect, useState } from "react";
import axios from "axios";
import { Info, Zap, Loader2 } from "lucide-react";
import { RoutingDecisionDetail } from "./RoutingDecisionDetail";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Rail colors — canonical across the product (Spec: #14b8a6 / #3b82f6 / #a855f7 / #10b981)
const RAIL_STYLE = {
  FEDNOW:            { label: "FedNow",               text: "text-teal-600",   ring: "ring-teal-500/20",    dot: "bg-teal-500" },
  RTP:               { label: "RTP",                  text: "text-blue-600",   ring: "ring-blue-500/20",    dot: "bg-blue-500" },
  STABLECOIN:        { label: "Stablecoin Express",   text: "text-purple-600", ring: "ring-purple-500/20",  dot: "bg-purple-500" },
  TOKENIZED_DEPOSIT: { label: "Tokenized Settlement", text: "text-emerald-600",ring: "ring-emerald-500/20", dot: "bg-emerald-500" },
};

const fmtRelative = (iso) => {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diff = Date.now() - t;
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
  const d = Math.round(h / 24);
  if (d === 1) return "yesterday";
  if (d < 30) return `${d} days ago`;
  return new Date(iso).toLocaleDateString();
};

export const RecentRoutingDecisions = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInfo, setShowInfo] = useState(false);
  const [openDecisionId, setOpenDecisionId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/v2/router-decisions/recent?limit=3`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ decisions: [], count: 0 }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="card-surface p-6 flex items-center justify-center" style={{ minHeight: 220 }}>
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  // Hide entirely when zero decisions
  if (!data || data.count === 0) return null;

  const decisions = data.decisions;
  // Pad to 3 slots, show friendly empty state for unfilled rows
  const padded = [...decisions];
  while (padded.length < 3) padded.push(null);

  return (
    <div className="card-surface p-6" data-testid="recent-routing-decisions">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-[#007377]" />
            <h2 className="text-lg font-bold text-slate-900">Recent Routing Decisions</h2>
            <div className="relative">
              <button
                onMouseEnter={() => setShowInfo(true)}
                onMouseLeave={() => setShowInfo(false)}
                onClick={() => setShowInfo((s) => !s)}
                className="text-slate-400 hover:text-slate-600 transition"
                aria-label="About routing decisions"
                data-testid="recent-routing-info-btn"
              >
                <Info className="w-4 h-4" />
              </button>
              {showInfo && (
                <div className="absolute left-6 top-0 z-10 w-64 bg-slate-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg" role="tooltip">
                  Our Smart Router picks the best rail for every transaction based on speed, cost, and recipient capability.
                </div>
              )}
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-0.5">How your payments were routed</p>
        </div>
      </div>

      <div className="divide-y divide-slate-100" data-testid="recent-routing-list">
        {padded.map((d, idx) => {
          if (!d) {
            // Empty row placeholder when fewer than 3 decisions exist
            return (
              <div key={`empty-${idx}`} className="flex items-center py-3 text-sm text-slate-300" data-testid={`recent-routing-empty-${idx}`}>
                <span className="italic">Make a payment to see how our Smart Router optimizes your money movement.</span>
              </div>
            );
          }
          const style = RAIL_STYLE[d.chosen_rail?.toUpperCase()] ||
                        { label: d.rail_display || "Smart Router", text: "text-slate-700", ring: "ring-slate-300/20", dot: "bg-slate-500" };
          return (
            <button
              key={d.decision_id}
              type="button"
              onClick={() => setOpenDecisionId(d.decision_id)}
              className="w-full flex items-center gap-4 py-3 -mx-2 px-2 rounded-lg hover:bg-slate-50 transition cursor-pointer text-left"
              data-testid={`recent-routing-row-${idx}`}
              aria-label="View routing decision details"
            >
              {/* Left: rail badge */}
              <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white border border-slate-200 ring-1 ${style.ring} flex-shrink-0`}>
                <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
                <span className={`text-[11px] font-bold uppercase tracking-wide ${style.text}`}>{style.label}</span>
                <span className={`${style.text} text-xs`} aria-hidden>✓</span>
              </div>
              {/* Middle: rationale */}
              <p className="flex-1 text-sm text-slate-700 truncate" data-testid={`recent-routing-rationale-${idx}`}>
                {d.member_rationale}
              </p>
              {/* Right: relative timestamp */}
              <span className="text-xs text-slate-400 flex-shrink-0 whitespace-nowrap" data-testid={`recent-routing-time-${idx}`}>
                {fmtRelative(d.timestamp)}
              </span>
            </button>
          );
        })}
      </div>

      {openDecisionId && (
        <RoutingDecisionDetail
          decisionId={openDecisionId}
          onClose={() => setOpenDecisionId(null)}
        />
      )}
    </div>
  );
};
